import { useCallback, useEffect, useMemo, useRef } from "react";
import { useViewAmbientContext } from "../AmbientsContext";
import { KTSVG } from "../../../../../../../../_metronic/helpers";
import { useViewLandContext } from "../../ViewLandContext";
import { useMapLegendContext } from "../../Helpers/MapLegend";
import { useViewImageContext } from "../../Images/ViewImageContext";
import { useMapLayersV2 } from "../../../../../Map/MapLayersV2Context";
import { useTranslation } from "react-i18next";
import { Divider } from '@material-ui/core';
import { openAlert } from "../../../../../../../../_metronic/elements/NotificationAlert";
import { useIsMobile } from "../../../../../../../shared/responsive";
import { useGisContext } from "../../../../../GisContext";
import { applyAmbientToMap } from "../ambientMapUtils";

const ViewAmbient = () => {
  const {
    ambientData,
    ambientHdData,
    ambientSummary,
    setDeleteAmbient,
    updateAmbientDialog,
    imageAmbientData,
    imageAmbientHdData,
    selectedElement,
    setSelectedElement,
  } = useViewAmbientContext();
  const viewLandContext = useViewLandContext();
  const { land } = viewLandContext;
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { closePanel } = useGisContext();
  const mapLayers = useMapLayersV2();
  const mapLegendContext = useMapLegendContext();
  const {
    openedAmbients,
    setOpenPrescription,
    addOpenedAmbients,
    activeAmbientSelection,
    setActiveAmbientSelection,
  } = useViewImageContext();
  const hasHistoricalGeojson = ambientSummary.hasHistoricalAmbient;
  const hasHistoricalHdGeojson = ambientSummary.hasHistoricalHdAmbient;
  const hasImageGeojson = ambientSummary.hasImageAmbient;
  const hasImageHdGeojson = ambientSummary.hasImageAmbientHd;
  const lastAppliedSelectionRef = useRef(null);
  const removeAmbientRef = useRef(mapLayers.land.ambient.remove);
  const hideLegendRef = useRef(mapLegendContext.hideLegend);
  const selectedElementRef = useRef(selectedElement);
  const isHistoricalOpen = selectedElement === 'ambient' && openedAmbients.includes(land.land_id);
  const isHistoricalHdOpen = selectedElement === 'ambientHd' && openedAmbients.includes(land.land_id);
  const isImageAmbientOpen = selectedElement === 'imageAmbient' && openedAmbients.includes(land.land_id);
  const isImageAmbientHdOpen = selectedElement === 'imageAmbientHd' && openedAmbients.includes(land.land_id);
  const isAmbientOpen = isHistoricalOpen || isHistoricalHdOpen || isImageAmbientOpen || isImageAmbientHdOpen;
  const opened = isAmbientOpen;
  const hasVisibleAmbientForCurrentLand =
    mapLegendContext.activeLand === land.land_id && mapLegendContext.ambient != null;

  useEffect(() => {
    removeAmbientRef.current = mapLayers.land.ambient.remove;
  }, [mapLayers.land.ambient.remove]);

  useEffect(() => {
    hideLegendRef.current = mapLegendContext.hideLegend;
  }, [mapLegendContext.hideLegend]);

  useEffect(() => {
    selectedElementRef.current = selectedElement;
  }, [selectedElement]);

  const applyAmbientSelection = useCallback((ambient, selectionKey) => {
    const applied = applyAmbientToMap({
      ambient,
      landId: land.land_id,
      mapLayers,
      mapLegendContext,
      addOpenedAmbients,
    });

    if (!applied) {
      return;
    }

    lastAppliedSelectionRef.current = selectionKey;

    if (isMobile) {
      closePanel();
    }
  }, [addOpenedAmbients, closePanel, isMobile, land.land_id, mapLayers, mapLegendContext]);

  const selectAmbient = useCallback((ambient, selectionKey, selectionType) => {
    if (!ambient?.geojson?.features?.length) {
      return;
    }

    setSelectedElement(selectionType);
    setActiveAmbientSelection({
      landId: land.land_id,
      type: selectionType,
    });
    applyAmbientSelection(ambient, selectionKey);
    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        applyAmbientSelection(ambient, selectionKey);
      }, 0);
    });
  }, [applyAmbientSelection, land.land_id, setActiveAmbientSelection, setSelectedElement]);

  const historicalSelectionKey = useMemo(() => {
    if (!hasHistoricalGeojson) {
      return null;
    }

    return [
      "ambient",
      land.land_id,
      ambientData?.ambient_id || "no-id",
      ambientData?.status ?? "na",
      ambientData?.geojson?.features?.length || 0,
    ].join(":");
  }, [ambientData?.ambient_id, ambientData?.geojson?.features?.length, ambientData?.status, hasHistoricalGeojson, land.land_id]);

  const historicalHdSelectionKey = useMemo(() => {
    if (!hasHistoricalHdGeojson) {
      return null;
    }

    return [
      "ambientHd",
      land.land_id,
      ambientHdData?.ambient_id || "no-id",
      ambientHdData?.status ?? "na",
      ambientHdData?.geojson?.features?.length || 0,
    ].join(":");
  }, [ambientHdData?.ambient_id, ambientHdData?.geojson?.features?.length, ambientHdData?.status, hasHistoricalHdGeojson, land.land_id]);

  const imageSelectionKey = useMemo(() => {
    if (!hasImageGeojson) {
      return null;
    }

    return [
      "imageAmbient",
      land.land_id,
      imageAmbientData?.ambient_id || "no-id",
      imageAmbientData?.status ?? "na",
      imageAmbientData?.geojson?.features?.length || 0,
    ].join(":");
  }, [hasImageGeojson, imageAmbientData?.ambient_id, imageAmbientData?.geojson?.features?.length, imageAmbientData?.status, land.land_id]);

  const imageHdSelectionKey = useMemo(() => {
    if (!hasImageHdGeojson) {
      return null;
    }

    return [
      "imageAmbientHd",
      land.land_id,
      imageAmbientHdData?.ambient_id || "no-id",
      imageAmbientHdData?.status ?? "na",
      imageAmbientHdData?.geojson?.features?.length || 0,
    ].join(":");
  }, [hasImageHdGeojson, imageAmbientHdData?.ambient_id, imageAmbientHdData?.geojson?.features?.length, imageAmbientHdData?.status, land.land_id]);

  const viewAmbient = useCallback(() => {
    if (!hasHistoricalGeojson) {
      return;
    }

    selectAmbient(ambientData, historicalSelectionKey, 'ambient');
  }, [ambientData, hasHistoricalGeojson, historicalSelectionKey, selectAmbient]);

  const viewAmbientHd = useCallback(() => {
    if (!hasHistoricalHdGeojson) {
      return;
    }

    selectAmbient(ambientHdData, historicalHdSelectionKey, 'ambientHd');
  }, [ambientHdData, hasHistoricalHdGeojson, historicalHdSelectionKey, selectAmbient]);

  const updateAmbient = useCallback(() => {
    if (!ambientData?.editable) {
      openAlert("danger", "Error", t("ambient:view.messages.cant_update_old_ambient"));
      return;
    }
    updateAmbientDialog();
  }, [ambientData?.editable, t, updateAmbientDialog]);

  const viewImageAmbient = useCallback(() => {
    if (!hasImageGeojson) {
      return;
    }

    selectAmbient(imageAmbientData, imageSelectionKey, 'imageAmbient');
  }, [hasImageGeojson, imageAmbientData, imageSelectionKey, selectAmbient]);

  const viewImageAmbientHd = useCallback(() => {
    if (!hasImageHdGeojson) {
      return;
    }

    selectAmbient(imageAmbientHdData, imageHdSelectionKey, 'imageAmbientHd');
  }, [hasImageHdGeojson, imageAmbientHdData, imageHdSelectionKey, selectAmbient]);

  useEffect(() => {
    return () => {
      const hasSelectedAmbient =
        selectedElementRef.current === "ambient"
        || selectedElementRef.current === "ambientHd"
        || selectedElementRef.current === "imageAmbient"
        || selectedElementRef.current === "imageAmbientHd";

      if (!hasSelectedAmbient) {
        return;
      }

      removeAmbientRef.current({
        landId: land.land_id
      });
      hideLegendRef.current(land.land_id);
    };
  }, [land.land_id]);

  useEffect(() => {
    if (viewLandContext.activeSection !== "ambients") {
      return;
    }

    const hasPersistedSelectionForCurrentLand =
      activeAmbientSelection?.landId === land.land_id &&
      activeAmbientSelection?.type === selectedElement;

    if (!hasPersistedSelectionForCurrentLand) {
      return;
    }

    if (selectedElement === 'ambient' && hasHistoricalGeojson) {
      const needsHistoricalSync =
        !isHistoricalOpen ||
        !hasVisibleAmbientForCurrentLand ||
        lastAppliedSelectionRef.current !== historicalSelectionKey;

      if (needsHistoricalSync) {
        viewAmbient();
      }
      return;
    }

    if (selectedElement === 'ambientHd' && hasHistoricalHdGeojson) {
      const needsHistoricalHdSync =
        !isHistoricalHdOpen ||
        !hasVisibleAmbientForCurrentLand ||
        lastAppliedSelectionRef.current !== historicalHdSelectionKey;

      if (needsHistoricalHdSync) {
        viewAmbientHd();
      }
      return;
    }

    if (selectedElement === 'imageAmbientHd' && hasImageHdGeojson) {
      const needsImageAmbientHdSync =
        !isImageAmbientHdOpen ||
        !hasVisibleAmbientForCurrentLand ||
        lastAppliedSelectionRef.current !== imageHdSelectionKey;

      if (needsImageAmbientHdSync) {
        viewImageAmbientHd();
      }
      return;
    }

    if (selectedElement !== 'imageAmbient' || !hasImageGeojson) {
      return;
    }

    const needsImageAmbientSync =
      !isImageAmbientOpen ||
      !hasVisibleAmbientForCurrentLand ||
      lastAppliedSelectionRef.current !== imageSelectionKey;

    if (needsImageAmbientSync) {
      viewImageAmbient();
    }
  }, [
    activeAmbientSelection?.landId,
    activeAmbientSelection?.type,
    hasHistoricalHdGeojson,
    hasHistoricalGeojson,
    hasImageGeojson,
    hasImageHdGeojson,
    hasVisibleAmbientForCurrentLand,
    historicalHdSelectionKey,
    historicalSelectionKey,
    imageSelectionKey,
    imageHdSelectionKey,
    isHistoricalHdOpen,
    isHistoricalOpen,
    isImageAmbientOpen,
    isImageAmbientHdOpen,
    land.land_id,
    selectedElement,
    viewAmbient,
    viewAmbientHd,
    viewImageAmbient,
    viewImageAmbientHd,
    viewLandContext.activeSection,
  ]);

  if (isMobile) {
    const hasAmbient = ambientSummary.hasHistoricalAmbient;
    const hasAmbientHd = ambientSummary.hasHistoricalHdAmbient;
    const hasImageAmbient = ambientSummary.hasImageAmbient;
    const hasImageAmbientHd = ambientSummary.hasImageAmbientHd;

    return (
      <div className="view-land-mobile-state-stack">
        {hasAmbient && (
          <button
            type="button"
            onClick={() => viewAmbient()}
            className={`view-land-mobile-state-card view-land-mobile-state-card--button ${selectedElement === "ambient" ? "active" : ""}`.trim()}
          >
            <span className="view-land-mobile-state-card__header">
              <span className="view-land-mobile-state-card__icon">
                <KTSVG path="/media/icons/duotune/maps/map002.svg" className="svg-icon svg-icon-2" />
              </span>
              <span className="view-land-mobile-state-card__copy">
                <span className="view-land-mobile-state-card__title">{t("ambient:view.buttons.view")}</span>
                <span className="view-land-mobile-state-card__status">
                  {ambientSummary.isViewingHistoricalAmbient && opened
                    ? t("ambient:view.status.showing_on_map")
                    : t("ambient:view.status.ready")}
                </span>
              </span>
            </span>
          </button>
        )}

        {hasAmbientHd && (
          <button
            type="button"
            onClick={() => viewAmbientHd()}
            className={`view-land-mobile-state-card view-land-mobile-state-card--button ${selectedElement === "ambientHd" ? "active" : ""}`.trim()}
          >
            <span className="view-land-mobile-state-card__header">
              <span className="view-land-mobile-state-card__icon">
                <KTSVG path="/media/icons/duotune/maps/map002.svg" className="svg-icon svg-icon-2" />
              </span>
              <span className="view-land-mobile-state-card__copy">
                <span className="view-land-mobile-state-card__title">{t("ambient:view.buttons.view_hd")}</span>
                <span className="view-land-mobile-state-card__status">
                  {ambientSummary.isViewingHistoricalHdAmbient && opened
                    ? t("ambient:view.status.showing_on_map")
                    : t("ambient:view.status.ready")}
                </span>
              </span>
            </span>
          </button>
        )}

        {hasImageAmbient && (
          <button
            type="button"
            onClick={() => viewImageAmbient()}
            className={`view-land-mobile-state-card view-land-mobile-state-card--button ${selectedElement === "imageAmbient" ? "active" : ""}`.trim()}
          >
            <span className="view-land-mobile-state-card__header">
              <span className="view-land-mobile-state-card__icon">
                <KTSVG path="/media/icons/duotune/maps/map002.svg" className="svg-icon svg-icon-2" />
              </span>
              <span className="view-land-mobile-state-card__copy">
                <span className="view-land-mobile-state-card__title">{t("img_ambient:view.buttons.view")}</span>
                <span className="view-land-mobile-state-card__status">
                  {ambientSummary.isViewingImageAmbient && opened
                    ? t("ambient:view.status.showing_on_map")
                    : t("ambient:view.status.ready")}
                </span>
              </span>
            </span>
          </button>
        )}

        {hasImageAmbientHd && (
          <button
            type="button"
            onClick={() => viewImageAmbientHd()}
            className={`view-land-mobile-state-card view-land-mobile-state-card--button ${selectedElement === "imageAmbientHd" ? "active" : ""}`.trim()}
          >
            <span className="view-land-mobile-state-card__header">
              <span className="view-land-mobile-state-card__icon">
                <KTSVG path="/media/icons/duotune/maps/map002.svg" className="svg-icon svg-icon-2" />
              </span>
              <span className="view-land-mobile-state-card__copy">
                <span className="view-land-mobile-state-card__title">{t("img_ambient:view.buttons.view")} HD</span>
                <span className="view-land-mobile-state-card__status">
                  {ambientSummary.isViewingImageAmbientHd && opened
                    ? t("ambient:view.status.showing_on_map")
                    : t("ambient:view.status.ready")}
                </span>
              </span>
            </span>
          </button>
        )}

        {opened && (
          <div className="view-land-mobile-state-card">
            <div className="view-land-mobile-state-card__copy">
              <span className="view-land-mobile-state-card__title">{t("ambient:view.actions.title")}</span>
              <span className="view-land-mobile-state-card__status">
                {ambientSummary.isViewingImageAmbient || ambientSummary.isViewingImageAmbientHd
                  ? t("ambient:view.status.image_active")
                  : ambientSummary.isViewingHistoricalHdAmbient
                    ? t("ambient:view.status.historical_hd_active")
                  : t("ambient:view.status.historical_active")}
              </span>
            </div>
            <div className="view-land-mobile-state-card__actions">
              <button type="button" onClick={() => setOpenPrescription(true)} className="btn btn-sm btn-light-primary">
                {t("ambient:view.buttons.create_prescription")}
              </button>
              {ambientData && ambientSummary.isViewingHistoricalAmbient && (
                <button
                  type="button"
                  onClick={() => updateAmbient()}
                  className={`btn btn-sm ${ambientData.editable ? "btn-light-warning" : "btn-light-secondary"}`.trim()}
                >
                  {t("ambient:view.buttons.update")}
                </button>
              )}
              <button type="button" onClick={() => setDeleteAmbient(true)} className="btn btn-sm btn-light-danger">
                {t("ambient:view.buttons.delete")}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
      <>
        {(ambientData && ambientData.geojson) &&<div onClick={() => viewAmbient()} className={`d-flex align-items-center div-type-selector rounded p-5 ${selectedElement === 'ambient'? 'active' : ''}`}>
          <span className="svg-icon svg-icon-warning me-5">
            <KTSVG path="/media/icons/duotune/maps/map002.svg" />
          </span>
          <div className="flex-grow-1 me-2">
            <span className="fw-bold text-gray-800 text-hover-primary fs-7">{t("ambient:view.buttons.view")}</span>
          </div>
        </div>}
        {hasHistoricalHdGeojson &&<div onClick={() => viewAmbientHd()} className={`d-flex align-items-center div-type-selector rounded p-5 ${selectedElement === 'ambientHd'? 'active' : ''}`}>
          <span className="svg-icon svg-icon-warning me-5">
            <KTSVG path="/media/icons/duotune/maps/map002.svg" />
          </span>
          <div className="flex-grow-1 me-2">
            <span className="fw-bold text-gray-800 text-hover-primary fs-7">{t("ambient:view.buttons.view_hd")}</span>
          </div>
        </div>}
        {hasImageGeojson && <div onClick={() => viewImageAmbient()} className={`d-flex align-items-center div-type-selector rounded p-5 ${selectedElement === 'imageAmbient'? 'active' : ''}`}>
          <span className="svg-icon svg-icon-warning me-5">
            <KTSVG path="/media/icons/duotune/maps/map002.svg" />
          </span>
          <div className="flex-grow-1 me-2">
            <span className="fw-bold text-gray-800 text-hover-primary fs-7">{t("img_ambient:view.buttons.view")}</span>
          </div>
        </div>}
        {hasImageHdGeojson && <div onClick={() => viewImageAmbientHd()} className={`d-flex align-items-center div-type-selector rounded p-5 ${selectedElement === 'imageAmbientHd'? 'active' : ''}`}>
          <span className="svg-icon svg-icon-warning me-5">
            <KTSVG path="/media/icons/duotune/maps/map002.svg" />
          </span>
          <div className="flex-grow-1 me-2">
            <span className="fw-bold text-gray-800 text-hover-primary fs-7">{t("img_ambient:view.buttons.view")} HD</span>
          </div>
        </div>}
        <div className="d-flex flex-column ms-4">
        {opened && (
          <>
          <Divider />
            <div onClick={() => setOpenPrescription(true)} className={`d-flex align-items-center div-type-selector rounded p-5`}>
              <span className="svg-icon svg-icon-warning me-5">
                <KTSVG path="/media/icons/duotune/communication/com008.svg" />
              </span>
              <div className="flex-grow-1 me-2">
                <span className="fw-bold text-gray-800 text-hover-primary fs-7">{t("ambient:view.buttons.create_prescription")}</span>
              </div>
            </div>
            {ambientData && selectedElement === 'ambient' && <div onClick={() => {
              updateAmbient()
            }} className={`d-flex align-items-center div-type-selector rounded p-5 ${!ambientData.editable  ? 'disabled' : ''}`}>
              <span className="svg-icon svg-icon-warning me-5">
                <KTSVG path="/media/icons/duotune/coding/cod009.svg" />
              </span>
              <div className="flex-grow-1 me-2">
                <span className="fw-bold text-gray-800 text-hover-primary fs-7">{t("ambient:view.buttons.update")}</span>
              </div>
            </div>}
            <div onClick={() => setDeleteAmbient(true)} className={`d-flex align-items-center div-type-selector rounded p-5`}>
              <span className="svg-icon svg-icon-warning me-5">
                <KTSVG path="/media/icons/duotune/art/art004.svg" />
              </span>
              <div className="flex-grow-1 me-2">
                <span className="fw-bold text-gray-800 text-hover-primary fs-7">{t("ambient:view.buttons.delete")}</span>
              </div>
            </div>
          </>
        )}
        </div>
      </>
  );
};

export default ViewAmbient;
